import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt, FaDollarSign, FaChartLine, FaUsers, FaUserTie,
  FaHourglassHalf, FaCheckCircle, FaTimesCircle, FaArrowUp,
  FaArrowRight, FaWallet, FaTicketAlt, FaShieldAlt, FaBell
} from 'react-icons/fa';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

const StatCard = ({ label, value, subtitle, icon, color, onClick, trend }) => (
  <div className={`dash-stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick} style={{ '--accent': color }}>
    <div className="dash-stat-icon" style={{ background: `${color}18`, color }}>
      {icon}
    </div>
    <div className="dash-stat-body">
      <p className="dash-stat-label">{label}</p>
      <h2 className="dash-stat-value">{value}</h2>
      <div className="dash-stat-foot">
        <span className="dash-stat-sub">{subtitle}</span>
        {trend !== undefined && (
          <span className={`dash-trend ${trend >= 0 ? 'up' : 'down'}`}>
            <FaArrowUp className={trend < 0 ? 'flipped' : ''} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('week');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [revenueChart, setRevenueChart] = useState(null);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [payoutStats, setPayoutStats] = useState(null);
  const [supportStats, setSupportStats] = useState(null);
  const [bookingStats, setBookingStats] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const today = new Date();
      const start = new Date();
      if (timeFilter === 'week') start.setDate(today.getDate() - 7);
      else if (timeFilter === 'month') start.setMonth(today.getMonth() - 1);
      else if (timeFilter === '3months') start.setMonth(today.getMonth() - 3);
      else start.setFullYear(today.getFullYear() - 1);

      const startDate = start.toISOString();
      const endDate = today.toISOString();

      // Choose groupBy based on time range for best chart readability
    const groupBy = timeFilter === 'week' ? 'day' : timeFilter === 'month' ? 'day' : timeFilter === '3months' ? 'week' : 'month';

    try {
        const [dashRes, revenueRes, pendingRes, recentRes, payoutRes, bookingAnalyticsRes] = await Promise.allSettled([
          api.get(`${ENDPOINTS.DASHBOARD.STATS}?startDate=${startDate}&endDate=${endDate}`),
          api.get(`/analytics/revenue?groupBy=${groupBy}&startDate=${startDate}&endDate=${endDate}`),
          api.get(ENDPOINTS.PROVIDERS.APPROVALS),
          api.get(ENDPOINTS.DASHBOARD.RECENT_BOOKINGS),
          api.get(ENDPOINTS.PAYOUTS.stats),
          api.get(`/analytics/bookings?startDate=${startDate}&endDate=${endDate}`),
        ]);

        if (dashRes.status === 'fulfilled') setStats(dashRes.value.data || dashRes.value);
        if (revenueRes.status === 'fulfilled') setRevenueChart(revenueRes.value.data || revenueRes.value);
        if (pendingRes.status === 'fulfilled') {
          const d = pendingRes.value.data || pendingRes.value;
          setPendingProviders(Array.isArray(d) ? d : (d?.data || []));
        }
        if (recentRes.status === 'fulfilled') {
          const d = recentRes.value.data || recentRes.value;
          setRecentBookings(Array.isArray(d) ? d : (d?.data || []));
        }
        if (payoutRes.status === 'fulfilled') setPayoutStats(payoutRes.value.data || payoutRes.value);
        // /analytics/bookings returns { total, statusCounts: { completed, pending, cancelled, confirmed, inProgress } }
        if (bookingAnalyticsRes.status === 'fulfilled') setBookingStats(bookingAnalyticsRes.value.data || bookingAnalyticsRes.value);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [timeFilter]);

  /* ─── Chart Config ─── */
  const revLabels = revenueChart?.labels?.length > 0 ? revenueChart.labels : [];
  const revData = revenueChart?.revenue?.length > 0 ? revenueChart.revenue : [];

  const lineData = {
    labels: revLabels,
    datasets: [{
      label: 'Revenue ($)',
      data: revData,
      backgroundColor: 'rgba(80, 200, 172, 0.12)',
      borderColor: '#50C8AC',
      borderWidth: 2.5,
      fill: true,
      tension: 0.45,
      pointRadius: 4,
      pointBackgroundColor: '#50C8AC',
      pointHoverRadius: 7,
    }]
  };

  const lineOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1e293b', padding: 14,
        titleColor: '#fff', bodyColor: '#94a3b8',
        borderColor: '#50C8AC', borderWidth: 1,
        displayColors: false,
        callbacks: { label: ctx => `$${ctx.parsed.y.toLocaleString()}` }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 11 } } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(226,232,240,0.6)', drawBorder: false },
        ticks: { color: '#94a3b8', font: { size: 11 }, callback: v => v >= 1000 ? `$${v / 1000}k` : `$${v}` }
      }
    }
  };

  // Booking Status Donut — sourced from /analytics/bookings which is date-aware
  const completedCount = bookingStats?.statusCounts?.completed || stats?.completedBookings || 0;
  const pendingCount   = (bookingStats?.statusCounts?.pending || 0) + (bookingStats?.statusCounts?.confirmed || 0);
  const cancelledCount = bookingStats?.statusCounts?.cancelled || 0;
  const inProgressCount = bookingStats?.statusCounts?.inProgress || 0;
  const donutData = {
    labels: ['Completed', 'Pending / Confirmed', 'In Progress', 'Cancelled'],
    datasets: [{
      data: [completedCount, pendingCount, inProgressCount, cancelledCount],
      backgroundColor: ['#10b981', '#f59e0b', '#3b82f6', '#ef4444'],
      borderWidth: 0,
      hoverOffset: 8,
    }]
  };
  const donutOptions = {
    responsive: true, maintainAspectRatio: false, cutout: '70%',
    plugins: {
      legend: { position: 'bottom', labels: { padding: 16, font: { size: 11 }, color: '#64748b', boxWidth: 12 } },
      tooltip: {
        backgroundColor: '#1e293b', padding: 12,
        callbacks: {
          label: ctx => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
          }
        }
      }
    }
  };

  // Total for donut centre label
  const totalBookingsInPeriod = completedCount + pendingCount + inProgressCount + cancelledCount;

  const bookingsArr = recentBookings.slice(0, 6);
  const approvalsArr = pendingProviders.slice(0, 8);

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner"/>
        <p>Fetching live data…</p>
      </div>
    );
  }

  return (
    <div className="dash-page">

      {/* ── Header ── */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Dashboard</h1>
          <p className="dash-subtitle">Welcome back — here's what's happening today</p>
        </div>
        <div className="dash-header-actions">
          <select className="dash-filter" value={timeFilter} onChange={e => setTimeFilter(e.target.value)}>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* ── Row 1: Primary KPIs ── */}
      <div className="dash-grid-4 mb-dash">
        <StatCard
          label="Total Bookings"
          value={stats?.totalBookings ?? '—'}
          subtitle={`${stats?.completedBookings ?? 0} completed`}
          icon={<FaCalendarAlt />}
          color="#50C8AC"
          onClick={() => navigate('/bookings')}
        />
        <StatCard
          label="GMV (Volume)"
          value={stats?.totalRevenue != null ? `$${Number(stats.totalRevenue).toLocaleString()}` : '—'}
          subtitle={`Avg $${stats?.avgOrderValue ?? 0} / order`}
          icon={<FaDollarSign />}
          color="#6366f1"
          onClick={() => navigate('/payments')}
        />
        <StatCard
          label="Total Platform Revenue"
          value={stats?.totalPlatformRevenue != null ? `$${Number(stats.totalPlatformRevenue).toLocaleString()}` : '—'}
          subtitle={`Inc: Comm + Tax + Fees`}
          icon={<FaChartLine />}
          color="#10b981"
          onClick={() => navigate('/payments')}
        />
        <StatCard
          label="Base Commissions"
          value={stats?.platformCommission != null ? `$${Number(stats.platformCommission).toLocaleString()}` : '—'}
          subtitle="Fixed % earnings"
          icon={<FaChartLine />}
          color="#f59e0b"
          onClick={() => navigate('/payments')}
        />
      </div>

      {/* ── Row 2: Secondary KPIs ── */}
      <div className="dash-grid-4 mb-dash">
        <StatCard
          label="Active Providers"
          value={stats?.activeProviders ?? '—'}
          subtitle="Fully approved"
          icon={<FaUserTie />}
          color="#10b981"
          onClick={() => navigate('/providers')}
        />
        <StatCard
          label="Active Customers"
          value={stats?.activeCustomers ?? '—'}
          subtitle="Non-blocked users"
          icon={<FaUsers />}
          color="#3b82f6"
          onClick={() => navigate('/customers')}
        />
        <StatCard
          label="Pending Approvals"
          value={pendingProviders.length}
          subtitle="Awaiting review"
          icon={<FaHourglassHalf />}
          color="#ef4444"
          onClick={() => navigate('/providers/approval')}
        />
        <StatCard
          label="Pending Payouts"
          value={payoutStats?.pendingPayouts ?? '—'}
          subtitle={`$${Number(payoutStats?.totalPaidOut ?? 0).toLocaleString()} paid total`}
          icon={<FaWallet />}
          color="#8b5cf6"
          onClick={() => navigate('/payouts')}
        />
      </div>

      {/* ── Row 3: Revenue Chart + Donut ── */}
      <div className="dash-grid-chart mb-dash">
        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <h3>Revenue Trend</h3>
              <p className="dash-card-sub">Platform GMV over time</p>
            </div>
          </div>
          <div style={{ height: 280 }}>
            {revData.length > 0
              ? <Line data={lineData} options={lineOptions} />
              : <div className="dash-empty-chart">No revenue data for this period</div>
            }
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <h3>Booking Status</h3>
              <p className="dash-card-sub">
                {totalBookingsInPeriod > 0 ? `${totalBookingsInPeriod} bookings` : 'No bookings'} in selected period
              </p>
            </div>
          </div>
          <div style={{ height: 280, position: 'relative' }}>
            {totalBookingsInPeriod > 0
              ? (
                <>
                  <Doughnut data={donutData} options={donutOptions} />
                  <div className="donut-center-label">
                    <span className="donut-total">{totalBookingsInPeriod}</span>
                    <span className="donut-label-sub">Total</span>
                  </div>
                </>
              )
              : <div className="dash-empty-chart">No booking data for this period</div>
            }
          </div>
        </div>
      </div>

      {/* ── Row 4: Pending Approvals + Recent Bookings ── */}
      <div className="dash-grid-chart mb-dash">
        {/* Pending Approvals */}
        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <h3>Pending Approvals</h3>
              <p className="dash-card-sub">{approvalsArr.length} provider{approvalsArr.length !== 1 ? 's' : ''} awaiting review</p>
            </div>
            <button className="dash-link-btn" onClick={() => navigate('/providers/approval')}>
              View All <FaArrowRight />
            </button>
          </div>
          {approvalsArr.length === 0 ? (
            <div className="dash-empty-state">
              <FaCheckCircle style={{ color: '#10b981', fontSize: 32 }} />
              <p>All providers are reviewed!</p>
            </div>
          ) : (
            <div className="dash-approvals-list">
              {approvalsArr.map(p => (
                <div key={p.id} className="dash-approval-row" onClick={() => navigate(`/providers/${p.id}`)}>
                  <img
                    src={p.avatar || `https://ui-avatars.com/api/?name=${p.firstName}+${p.lastName}&background=50C8AC&color=fff&size=40`}
                    alt={p.firstName}
                    className="dash-approval-avatar"
                  />
                  <div className="dash-approval-info">
                    <strong>{p.firstName} {p.lastName}</strong>
                    <span>{p.businessName || p.email}</span>
                  </div>
                  <div className="dash-approval-meta">
                    <span className="dash-badge pending">Pending</span>
                    <span className="dash-approval-date">{new Date(p.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Bookings */}
        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <h3>Recent Bookings</h3>
              <p className="dash-card-sub">Latest platform activity</p>
            </div>
            <button className="dash-link-btn" onClick={() => navigate('/bookings')}>
              View All <FaArrowRight />
            </button>
          </div>
          {bookingsArr.length === 0 ? (
            <div className="dash-empty-state">
              <FaCalendarAlt style={{ color: '#94a3b8', fontSize: 32 }} />
              <p>No recent bookings found</p>
            </div>
          ) : (
            <div className="dash-booking-list">
              {bookingsArr.map(b => {
                const statusMap = {
                  completed: '#10b981', confirmed: '#3b82f6',
                  pending: '#f59e0b', cancelled: '#ef4444', pending_payment: '#8b5cf6'
                };
                const st = (b.status || 'pending').toLowerCase();
                return (
                  <div key={b.id} className="dash-booking-row" onClick={() => navigate(`/bookings/${b.id}`)}>
                    <img
                      src={b.customer?.avatar || `https://ui-avatars.com/api/?name=${b.customer?.firstName || 'U'}+${b.customer?.lastName || ''}&background=6366f1&color=fff&size=36`}
                      alt="customer"
                      className="dash-booking-avatar"
                    />
                    <div className="dash-booking-info">
                      <strong>{b.customer?.firstName} {b.customer?.lastName}</strong>
                      <span>{b.service?.name || 'Service'}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className="dash-status-dot" style={{ background: statusMap[st] || '#94a3b8' }}>
                        {b.status}
                      </span>
                      <p className="dash-booking-date">{new Date(b.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 5: Quick Access ── */}
      <div className="dash-card mb-dash">
        <h3 style={{ marginBottom: 24, color: 'var(--text-primary)' }}>Quick Access</h3>
        <div className="dash-quick-grid">
          {[
            { label: 'Bookings', icon: <FaCalendarAlt />, path: '/bookings', color: '#50C8AC' },
            { label: 'Providers', icon: <FaUserTie />, path: '/providers', color: '#6366f1' },
            { label: 'Customers', icon: <FaUsers />, path: '/customers', color: '#3b82f6' },
            { label: 'Payments', icon: <FaDollarSign />, path: '/payments', color: '#f59e0b' },
            { label: 'Payouts', icon: <FaWallet />, path: '/payouts', color: '#8b5cf6' },
            { label: 'Disputes', icon: <FaTicketAlt />, path: '/support/tickets', color: '#ef4444' },
           // { label: 'Analytics', icon: <FaChartLine />, path: '/analytics', color: '#10b981' },
            { label: 'Loyalty', icon: <FaShieldAlt />, path: '/loyalty', color: '#ec4899' },
          ].map(q => (
            <div key={q.path} className="dash-quick-card" onClick={() => navigate(q.path)} style={{ '--q-color': q.color }}>
              <div className="dash-quick-icon" style={{ background: `${q.color}18`, color: q.color }}>
                {q.icon}
              </div>
              <span>{q.label}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;